import axios from "axios";
import { ElNotification } from "element-plus";
import { attachTokenRefresh } from "@/utils/authInterceptors";
// Optional Sentry import
let Sentry;
try {
  Sentry = require("@sentry/vue");
} catch (e) {
  console.log("Sentry not available");
}
import store from "@/store";
import router from "@/router";
import { getServiceConfigHeaders } from "@/utils/requestConfig";
import { CLOUD_SERVICE_BASE_URL } from "@/utils/const";
import { CLOUD_SERVICE_BASE_URL_V2 } from "@/utils/const";

// Handle auth errors - adapted for EHR
function logoutUser(reason) {
  store.dispatch('login/handleAuthError');
}

const orderTestServiceCloud = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === "production"
    ? "/portal/order" // production
    : "/portal/order/staging" //staging
  )
});

orderTestServiceCloud.interceptors.request.use(
  async function(config) {
    config.headers = await getServiceConfigHeaders();
    // update jwt token in body if any
    if (config.data && config.data.jwtToken) {
      config.data.jwtToken = sessionStorage.getItem("jwtToken");
    }
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

orderTestServiceCloud.interceptors.response.use(
  response => {
    const res = response.data;
    if (![200,204,201].includes(response.status)) {
      ElNotification({
        title: "Error",
        message: response.message,
        type: "error",
      });
      return Promise.reject("error");
    }
    else {
      return response;
    }
  },
  error => {
    console.log("err ", error);
    if (error.config && error.config.headers) {
      console.log("auth", error.config.headers.Authorization);
    }
    // If the request fails, capture the error with Sentry
    if (Sentry && Sentry.captureException) {
      Sentry.captureException(error, {
        extra: {
          requestBody: error.config.data,
          requestUrl: error.config.url,
          requestMethod: error.config.method,
        },
      });
    }
    if (!error.response) {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
      return Promise.reject(error);
    }
    if (error.response.status === 401) {
      logoutUser(error.response.data.reason);
      return Promise.reject(error);
    }
    // not display notification if isPatientValidFollowUpOrder api
    let requestUrl = error.config.url;
    if (requestUrl.includes("/orderTest/isPatientValidFollowUpOrder")) {
      return Promise.reject(error);
    }
    // not display notification if post patient api & status code is 409
    if (requestUrl.includes("orderTest/patient") && error.response.status === 409 &&  error.config.method === "post") {
      return Promise.reject(error);
    }
    // check type for error.response.data first
    if (error.response.data && typeof error.response.data === "object") {
      let { reason, trace } = error.response.data;
      if (trace) {
        // show backend customized error message
        // get the first line error message & split with ": "
        let firstLineErrorSegment = trace.split(/[\n]/)[0].split(": ");
        // remove the first segment
        firstLineErrorSegment.splice(0, 1);
        // combine rest segment to form a new message
        let message = firstLineErrorSegment.join(": ");
        ElNotification({
          title: "Error",
          message,
          type: "error",
          duration: 8000
        });
      }
      else if (reason) {
        // special case: not show notification popup for "generate pdf failure error"
        if (reason.includes("generate pdf failure")) {
          return Promise.reject(error);
        }  
        ElNotification({
          title: "Error",
          message: reason,
          type: "error",
          duration: 8000
        });
      }
      else {
        ElNotification({
          title: "Error",
          message: error.message,
          type: "error",
          duration: 8000
        });
      }
    }
    else {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
    }
    return Promise.reject(error);
  }
);


export const orderTestServiceCloudV2 = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL_V2 + (process.env.VUE_APP_ENV === "production"
    ? "/portal/order" // production
    : "/portal/order-staging" //staging
  )
});

orderTestServiceCloudV2.interceptors.request.use(
  async function(config) {
    config.headers = await getServiceConfigHeaders();
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

orderTestServiceCloudV2.interceptors.response.use(
  response => {
    const res = response.data;
    if (![200,204,201].includes(response.status)) {
      ElNotification({
        title: "Error",
        message: response.message,
        type: "error",
      });
      return Promise.reject("error");
    }
    else {
      return response;
    }
  },
  error => {
    console.log("err ", error);
    // If the request fails, capture the error with Sentry
    if (Sentry && Sentry.captureException) {
      Sentry.captureException(error, {
        extra: {
          requestBody: error.config.data,
          requestUrl: error.config.url,
          requestMethod: error.config.method,
        },
      });
    }
    if (!error.response) {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
      return Promise.reject(error);
    }
    if (error.response.status === 401) {
      logoutUser(error.response.data.error);
      return Promise.reject(error);
    }
    // check type for error.response.data first
    if (error.response.data && typeof error.response.data === "object") {
      let { errorMessage } = error.response.data;
      let message = error.mssage;
      message = errorMessage ? errorMessage : message;
      ElNotification({
        title: "Error",
        message,
        type: "error",
        duration: 8000
      });
    }
    else {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
    }
    return Promise.reject(error);
  }
);

const newChargingServiceCloud = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === "production"
    ? "/charging" // production
    : "/charging/staging" //staging
  )
});

newChargingServiceCloud.interceptors.request.use(
  async function(config) {
    config.headers = await getServiceConfigHeaders();
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

newChargingServiceCloud.interceptors.response.use(
  response => {
    const res = response.data;
    if (![200,204,201].includes(response.status)) {
      ElNotification({
        title: "Error",
        message: response.message,
        type: "error",
      });
      return Promise.reject("error");
    }
    else {
      return response;
    }
  },
  error => {
    console.log("err ", error);
    // If the request fails, capture the error with Sentry
    if (Sentry && Sentry.captureException) {
      Sentry.captureException(error, {
        extra: {
          requestBody: error.config.data,
          requestUrl: error.config.url,
          requestMethod: error.config.method,
        },
      });
    }
    if (!error.response) {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
      return Promise.reject(error);
    }
    if (error.response.status === 401) {
      logoutUser(error.response.data.error);
      return Promise.reject(error);
    }
    // check type for error.response.data first
    if (error.response.data && typeof error.response.data === "object") {
      let { errorMessage } = error.response.data;
      let message = error.mssage;
      message = errorMessage ? errorMessage : message;
      ElNotification({
        title: "Error",
        message,
        type: "error",
        duration: 8000
      });
    }
    else {
      ElNotification({
        title: "Error",
        message: error.message,
        type: "error",
        duration: 8000,
      });
    }
    return Promise.reject(error);
  }
);


attachTokenRefresh(orderTestServiceCloud);
attachTokenRefresh(orderTestServiceCloudV2);
attachTokenRefresh(newChargingServiceCloud);

export { orderTestServiceCloud, newChargingServiceCloud };